import React from "react";

/**
 * Controlled text input that notifies parent after `debounce` ms.
 * Skips the initial mount callback so parents are not spammed with setState
 * (avoids update-depth loops when combined with unstable parent handlers).
 * Does not call onChange after unmount (avoids setState during route changes).
 */
const DebouncedInput = ({
    initialValue,
    value: valueProp,
    onChange,
    debounce = 500,
    ...props
}) => {
    const startValue = initialValue ?? valueProp ?? "";
    const [value, setValue] = React.useState(startValue);
    const isFirstRun = React.useRef(true);
    const mountedRef = React.useRef(true);
    const onChangeRef = React.useRef(onChange);

    React.useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    React.useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    React.useEffect(() => {
        setValue(initialValue ?? valueProp ?? "");
    }, [initialValue, valueProp]);

    React.useEffect(() => {
        if (isFirstRun.current) {
            isFirstRun.current = false;
            return undefined;
        }

        const timeout = setTimeout(() => {
            if (!mountedRef.current) {
                return;
            }
            onChangeRef.current?.(value || "");
        }, debounce);

        return () => clearTimeout(timeout);
    }, [value, debounce]);

    return (
        <input
            {...props}
            value={value || ""}
            onChange={(event) => setValue(event.target.value)}
        />
    );
};

export default DebouncedInput;
